// Imports
import React from 'react';
import styled, { css, useTheme } from 'styled-components/macro';
import { setLightness, setSaturation, readableColor, parseToHsl } from 'polished';
import { GeneratedThemeProps } from '../../../templates/template-style';


// Styled components
interface WrapperProps {
	round?: boolean;
	backgroundColor: string;
}

const Wrapper = styled.span<WrapperProps>`
	color: ${({ backgroundColor }) => readableColor(backgroundColor)};
	background-color: ${({ backgroundColor }) => backgroundColor};
	font-weight: 600;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	
	${({ round }) =>
		round &&
		css`
			border-radius: 50%;
			width: 2.5em;
			height: 2.5em;
		`}
`;


// Define the accepted props
interface Props extends Omit<WrapperProps, 'backgroundColor'> {
	firstName?: string;
	lastName?: string;
	useDefaultColor?: true;
}

type CompleteProps = Props & Omit<React.HTMLAttributes<HTMLSpanElement>, 'children' | 'ref'>;


// Function component
const UserAvatar: React.FC<CompleteProps> = ({ firstName, lastName, useDefaultColor, round, ...props }) => {
	// Initialize
	const theme = useTheme();
	
	
	// Type guard
	function isGeneratedTheme(theme: unknown): theme is GeneratedThemeProps {
		return theme !== null && typeof theme === 'object' && 'baseColor' in theme;
	}
	
	if (!isGeneratedTheme(theme)) {
		throw new Error('This should not be possible');
	}
	
	
	// Build color spectrum
	const initialColor = theme.baseColor;
	
	const possibleColors = (() => {
		// Initialize
		const parsed = parseToHsl(initialColor);
		
		
		// Handle Paper template theme
		if (theme.isPaper) {
			return ['hsl(0, 0%, 75%)'];
		}
		
		
		// Handle gray-scale base color (Nature - Pebbles template theme)
		if (parsed.saturation === 0) {
			const pastelHues = [30, 50, 140, 210, 260, 340];
			
			return pastelHues.map((hue) => `hsl(${hue}, 50%, 80%)`);
		}
		
		
		// Handle all other template themes
		const steps = [0.3, 0.4, 0.5, 0.6, 0.7, 0.8];
		
		return steps.flatMap((saturation) =>
			steps.map((lightness) => setLightness(lightness, setSaturation(saturation, initialColor)))
		);
	})();
	
	
	// Generate a background color from bytes of provided name
	const possibleColorIndex =
		new TextEncoder()
			.encode(`${firstName ?? ''}${lastName ? ` ${lastName}` : ''}`)
			.reduce((acc, byte) => (acc += byte), 0) % possibleColors.length;
	
	const backgroundColor = useDefaultColor ? possibleColors[0] : firstName ? possibleColors[possibleColorIndex] : '#ddd';
	
	
	// Return JSX
	return (
		<Wrapper backgroundColor={backgroundColor} round={round} {...props}>
			{`${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase()}
		</Wrapper>
	);
};

export default UserAvatar;
